import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import AccountSummaryCards from './components/AccountSummaryCards';
import AccountToolbar from './components/AccountToolbar';
import AccountTreeItem from './components/AccountTreeItem';
import AccountModal from './components/AccountModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import Icon from '../../components/AppIcon';
import { getChartOfAccounts, createAccount, updateAccount, deleteAccount } from '../../services/accountService';
import { useCurrentCompany } from '../../hooks/useCompany';


const ChartOfAccounts = () => {
  const { data: company, isLoading: companyLoading } = useCurrentCompany();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getChartOfAccounts();
      setAccounts(data);
    } catch (err) {
      console.error('Error loading chart of accounts:', err);
      setError('Failed to load chart of accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [draggedAccount, setDraggedAccount] = useState(null);

  const flattenAccounts = (accountList) => {
    let result = [];
    accountList?.forEach(account => {
      result?.push(account);
      if (account?.children) {
        result = result?.concat(flattenAccounts(account?.children));
      }
    });
    return result;
  };

  const filteredAccounts = useMemo(() => {
    const filterAccounts = (accountList) => {
      return accountList?.filter(account => {
        const matchesSearch = searchQuery === '' || 
          account?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          account?.code?.includes(searchQuery);
        
        const matchesType = selectedType === '' || account?.type === selectedType;

        if (matchesSearch && matchesType) {
          if (account?.children) {
            account.children = filterAccounts(account?.children);
          }
          return true;
        }

        if (account?.children) {
          const filteredChildren = filterAccounts(account?.children);
          if (filteredChildren?.length > 0) {
            account.children = filteredChildren;
            return true;
          }
        }

        return false;
      });
    };

    return filterAccounts(JSON.parse(JSON.stringify(accounts)));
  }, [accounts, searchQuery, selectedType]);

  const handleAddAccount = () => {
    setSelectedAccount(null);
    setIsModalOpen(true);
  };

  const handleEditAccount = (account) => {
    setSelectedAccount(account);
    setIsModalOpen(true);
  };

  const handleDeleteAccount = (account) => {
    setSelectedAccount(account);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (account) => {
    try {
      await deleteAccount(account.id);
      
      const deleteAccountRecursive = (accountList, accountId) => {
        return accountList?.filter(acc => {
          if (acc?.id === accountId) {
            return false;
          }
          if (acc?.children) {
            acc.children = deleteAccountRecursive(acc?.children, accountId);
          }
          return true;
        });
      };

      setAccounts(prevAccounts => deleteAccountRecursive(prevAccounts, account?.id));
    } catch (error) {
      console.error('Error deleting account:', error);
      setError('Failed to delete account. Please try again.');
    }
  };

  const handleToggleActive = (account) => {
    const toggleActiveRecursive = (accountList) => {
      return accountList?.map(acc => {
        if (acc?.id === account?.id) {
          return { ...acc, isActive: !acc?.isActive };
        }
        if (acc?.children) {
          return { ...acc, children: toggleActiveRecursive(acc?.children) };
        }
        return acc;
      });
    };

    setAccounts(prevAccounts => toggleActiveRecursive(prevAccounts));
  };

  const handleSaveAccount = async (accountData) => {
    try {
      if (selectedAccount) {
        // Update existing account
        const updatedAccount = await updateAccount(selectedAccount.id, accountData);
        
        const updateAccountRecursive = (accountList) => {
          return accountList?.map(acc => {
            if (acc?.id === updatedAccount?.id) {
              return { ...acc, ...updatedAccount };
            }
            if (acc?.children) {
              return { ...acc, children: updateAccountRecursive(acc?.children) };
            }
            return acc;
          });
        };

        setAccounts(prevAccounts => updateAccountRecursive(prevAccounts));
      } else {
        // Create new account
        const newAccount = await createAccount(accountData);

        if (accountData?.parentId) {
          const addToParent = (accountList) => {
            return accountList?.map(acc => {
              if (acc?.id === accountData?.parentId) {
                return {
                  ...acc,
                  children: [...(acc?.children || []), newAccount]
                };
              }
              if (acc?.children) {
                return { ...acc, children: addToParent(acc?.children) };
              }
              return acc;
            });
          };

          setAccounts(prevAccounts => addToParent(prevAccounts));
        } else {
          setAccounts(prevAccounts => [...prevAccounts, newAccount]);
        }
      }
      
      // Refresh the accounts list to ensure we have the latest data
      await loadAccounts();
    } catch (error) {
      console.error('Error saving account:', error);
      setError('Failed to save account. Please try again.');
    }
  };

  const handleDragStart = (e, account) => {
    setDraggedAccount(account);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetAccount) => {
    e?.preventDefault();
    if (!draggedAccount || draggedAccount?.id === targetAccount?.id) return;

    console.log('Drag and drop functionality - would move', draggedAccount?.name, 'to', targetAccount?.name);
    setDraggedAccount(null);
  };

  const handleBulkImport = () => {
    console.log('Bulk import functionality would open file picker');
  };

  const handleExport = () => {
    console.log('Export functionality would download CSV/Excel file');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12 space-y-6 md:space-y-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading font-semibold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                Chart of Accounts
              </h1>
              <p className="font-body text-sm md:text-base text-muted-foreground">
                Manage your account structure and financial categories
              </p>
            </div>
          </div>

          <AccountSummaryCards accounts={accounts} company={company} />

          <AccountToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            onAddAccount={handleAddAccount}
            onBulkImport={handleBulkImport}
            onExport={handleExport}
          />

          {loading || companyLoading ? (
            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center animate-pulse">
                <Icon name="Loader" size={32} className="text-muted-foreground animate-spin" />
              </div>
              <p className="font-body text-base md:text-lg text-foreground mb-2">
                Loading chart of accounts...
              </p>
            </div>
          ) : error ? (
            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
                <Icon name="AlertCircle" size={32} className="text-error" />
              </div>
              <p className="font-body text-base md:text-lg text-foreground mb-2">
                {error}
              </p>
              <button
                onClick={loadAccounts}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-muted border-b border-border font-body font-medium text-sm text-muted-foreground">
                <div className="col-span-1">Code</div>
                <div className="col-span-4">Account Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2 text-right">Balance</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>

              <div className="divide-y divide-border">
                {filteredAccounts?.length > 0 ? (
                  filteredAccounts?.map((account) => (
                    <AccountTreeItem
                      key={account?.id}
                      account={account}
                      company={company}
                      onEdit={handleEditAccount}
                      onDelete={handleDeleteAccount}
                      onToggleActive={handleToggleActive}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    />
                  ))
                ) : (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="Search" size={32} className="text-muted-foreground" />
                    </div>
                    <p className="font-body text-base md:text-lg text-foreground mb-2">
                      No accounts found
                    </p>
                    <p className="caption text-muted-foreground">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <AccountModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveAccount}
        account={selectedAccount}
        allAccounts={flattenAccounts(accounts)}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        account={selectedAccount}
      />
      <QuickActionToolbar />
    </div>
  );
};

export default ChartOfAccounts;